import {
    getAuthorPagePath,
    normalizeInternalHref,
    resolveAuthorCardHref,
    resolvePostHref,
} from '@pulsex/utils';
import type { AuthorCardHrefLike, PostHrefLike } from '@pulsex/utils';
import pulseConfig from '@/pulsex.config';

/**
 * Technical SEO: every internal link must resolve to its final URL.
 *
 * The CMS still hands us `http://` absolutes, `/posts/...` and `/Author/...`
 * paths, and a `links.al-akhbar.com` short link on every post. Rendering those
 * as-is sends each internal link through a 301/308 hop (or off-domain), which
 * wastes crawl budget and loses link equity. These helpers apply the same
 * normalization the middleware redirect uses, so the hop never happens.
 */
export const siteLinkOptions = {
    clientUrl: pulseConfig.CLIENT_URL,
    clientDomain: pulseConfig.CLIENT_DOMAIN,
};

/** Normalizes a CMS or hard-coded href (menus, sections, banners). */
export const siteHref = (href: string | null | undefined, fallback = ''): string =>
    normalizeInternalHref(href, siteLinkOptions) || fallback;

/** Resolves a post card href to its canonical on-site path. */
export const postHref = (post: PostHrefLike | null | undefined, fallback = ''): string =>
    resolvePostHref(post, { ...siteLinkOptions, fallback });

/** Author profile href — `/author/{id}`, never a bare numeric root path. */
export const authorHref = (id: string | number | null | undefined, fallback = ''): string =>
    id || id === 0 ? getAuthorPagePath(id) : fallback;

/**
 * Author card href for a search result.
 *
 * Search results are SearchX documents, whose `id` is a GUID — `authorHref` would turn that
 * into `/author/{guid}`, which renders an empty "Author" page instead of the writer's. This
 * uses the canonical URL the result carries, and returns '' when there is nothing safe to
 * link to.
 */
export const authorCardHref = (
    post: AuthorCardHrefLike | null | undefined,
    fallback = ''
): string => resolveAuthorCardHref(post, { ...siteLinkOptions, fallback });
